"use client"

import React, { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { Button } from "../../components/ui/button"
import { Input } from "../../components/ui/input"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card"
import { Badge } from "../../components/ui/badge"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select"
import Navbar from "../../components/Navbar"
import Footer from "../../components/Footer"
import { useLanguage } from "../../contexts/LanguageContext"
import { 
  Search, 
  Filter, 
  Star, 
  Users, 
  Zap,
  ArrowRight,
  Package,
  Globe,
  Shield,
  TrendingUp
} from "lucide-react"

// Mock data for AI tools
const mockTools = [
  {
    id: 1,
    nameKey: "tools.smartCustomerService",
    descriptionKey: "tools.smartCustomerService.desc",
    categoryKey: "category.customerService",
    price: 299.99,
    priceType: "monthly",
    rating: 4.8,
    reviews: 1247,
    developer: "ServiceAI Pro",
    image: "https://images.unsplash.com/photo-1551434678-e076c223a692?w=400&h=300&fit=crop",
    tagKeys: ["tools.tags.customerAutomation", "tools.tags.multilingual", "tools.tags.intelligentRouting"],
    featured: true
  },
  {
    id: 2,
    nameKey: "tools.financialAnalysis",
    descriptionKey: "tools.financialAnalysis.desc",
    categoryKey: "category.financialManagement",
    price: 199.99,
    priceType: "monthly",
    rating: 4.9,
    reviews: 892,
    developer: "FinanceAI",
    image: "https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=400&h=300&fit=crop",
    tagKeys: ["tools.tags.financialAnalysis", "tools.tags.predictiveModeling", "tools.tags.riskControl"],
    featured: true
  },
  {
    id: 3,
    nameKey: "tools.hrManagement",
    descriptionKey: "tools.hrManagement.desc",
    categoryKey: "category.humanResources",
    price: 159.99,
    priceType: "monthly",
    rating: 4.7,
    reviews: 2156,
    developer: "HR Tech Solutions",
    image: "https://images.unsplash.com/photo-1521791136064-7986c2920216?w=400&h=300&fit=crop",
    tagKeys: ["tools.tags.recruitmentManagement", "tools.tags.performanceEvaluation", "tools.tags.employeeTraining"],
    featured: false
  },
  {
    id: 4,
    nameKey: "tools.supplyChain",
    descriptionKey: "tools.supplyChain.desc",
    categoryKey: "category.supplyChain",
    price: 399.99,
    priceType: "monthly",
    rating: 4.6,
    reviews: 567,
    developer: "SupplyChain AI",
    image: "https://images.unsplash.com/photo-1566576912321-d58ddd7a6088?w=400&h=300&fit=crop",
    tagKeys: ["tools.tags.inventoryManagement", "tools.tags.logisticsOptimization", "tools.tags.demandForecasting"],
    featured: false
  },
  {
    id: 5,
    nameKey: "tools.salesAssistant",
    descriptionKey: "tools.salesAssistant.desc",
    categoryKey: "category.salesManagement",
    price: 249.99,
    priceType: "monthly",
    rating: 4.5,
    reviews: 743,
    developer: "SalesAI",
    image: "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=400&h=300&fit=crop",
    tagKeys: ["tools.tags.leadManagement", "tools.tags.customerRelationships", "tools.tags.salesForecasting"],
    featured: false
  },
  {
    id: 6,
    nameKey: "tools.marketAnalysis",
    descriptionKey: "tools.marketAnalysis.desc",
    categoryKey: "category.marketAnalysis",
    price: 179.99,
    priceType: "monthly",
    rating: 4.4,
    reviews: 432,
    developer: "MarketIntel",
    image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=400&h=300&fit=crop",
    tagKeys: ["tools.tags.marketMonitoring", "tools.tags.competitorAnalysis", "tools.tags.trendPrediction"],
    featured: false
  },
  {
    id: 7,
    nameKey: "tools.productionOptimization",
    descriptionKey: "tools.productionOptimization.desc",
    categoryKey: "category.productionManagement",
    price: 499.99,
    priceType: "monthly",
    rating: 4.8,
    reviews: 678,
    developer: "Manufacturing AI",
    image: "https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=400&h=300&fit=crop",
    tagKeys: ["tools.tags.productionOptimization", "tools.tags.qualityControl", "tools.tags.equipmentMaintenance"],
    featured: true
  },
  {
    id: 8,
    nameKey: "tools.complianceMonitoring",
    descriptionKey: "tools.complianceMonitoring.desc",
    categoryKey: "category.complianceManagement",
    price: 349.99,
    priceType: "monthly",
    rating: 4.7,
    reviews: 345,
    developer: "ComplianceAI",
    image: "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=400&h=300&fit=crop",
    tagKeys: ["tools.tags.complianceChecking", "tools.tags.riskWarning", "tools.tags.auditTracking"],
    featured: false
  },
  {
    id: 9,
    nameKey: "tools.decisionPlatform",
    descriptionKey: "tools.decisionPlatform.desc",
    categoryKey: "category.decisionSupport",
    price: 599.99,
    priceType: "monthly",
    rating: 4.9,
    reviews: 234,
    developer: "DecisionAI",
    image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=400&h=300&fit=crop",
    tagKeys: ["tools.tags.dataAnalysis", "tools.tags.businessIntelligence", "tools.tags.decisionSupport"],
    featured: true
  },
  {
    id: 10,
    nameKey: "tools.projectManagement",
    descriptionKey: "tools.projectManagement.desc",
    categoryKey: "category.projectManagement",
    price: 129.99,
    priceType: "monthly",
    rating: 4.6,
    reviews: 1567,
    developer: "ProjectAI",
    image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=300&fit=crop",
    tagKeys: ["tools.tags.taskManagement", "tools.tags.progressMonitoring", "tools.tags.resourceOptimization"],
    featured: false
  },
  {
    id: 11,
    nameKey: "tools.knowledgeManagement",
    descriptionKey: "tools.knowledgeManagement.desc",
    categoryKey: "category.knowledgeManagement",
    price: 89.99,
    priceType: "monthly",
    rating: 4.5,
    reviews: 789,
    developer: "KnowledgeAI",
    image: "https://images.unsplash.com/photo-1481627834876-b7833e8f5570?w=400&h=300&fit=crop",
    tagKeys: ["tools.tags.knowledgeBase", "tools.tags.documentManagement", "tools.tags.intelligentRetrieval"],
    featured: false
  },
  {
    id: 12,
    nameKey: "tools.trainingPlatform",
    descriptionKey: "tools.trainingPlatform.desc",
    categoryKey: "category.trainingDevelopment",
    price: 79.99,
    priceType: "monthly",
    rating: 4.4,
    reviews: 456,
    developer: "TrainingAI",
    image: "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=400&h=300&fit=crop",
    tagKeys: ["tools.tags.onlineTraining", "tools.tags.personalizedLearning", "tools.tags.effectivenessEvaluation"],
    featured: false
  },
  {
    id: 13,
    nameKey: "tools.marketingAutomation",
    descriptionKey: "tools.marketingAutomation.desc",
    categoryKey: "category.marketingAutomation",
    price: 199.99,
    priceType: "monthly",
    rating: 4.7,
    reviews: 892,
    developer: "MarketingAI",
    image: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=400&h=300&fit=crop",
    tagKeys: ["tools.tags.marketingAutomation", "tools.tags.customerReach", "tools.tags.conversionOptimization"],
    featured: true
  }
]

const categories = ["All", "客户服务", "财务管理", "人力资源", "供应链", "销售管理", "市场分析", "生产管理", "合规管理", "决策支持", "项目管理", "知识管理", "培训发展", "营销自动化"]

export default function MarketplacePage() {
  const navigate = useNavigate()
  const { t } = useLanguage()
  const [tools, setTools] = useState(mockTools)
  const [searchTerm, setSearchTerm] = useState("")
  const [selectedCategory, setSelectedCategory] = useState("All")
  const [sortBy, setSortBy] = useState("featured")

  // Filter and sort tools
  useEffect(() => {
    let filteredTools = mockTools

    // Filter by search term
    if (searchTerm) {
      filteredTools = filteredTools.filter(tool =>
        tool.nameKey.toLowerCase().includes(searchTerm.toLowerCase()) ||
        tool.descriptionKey.toLowerCase().includes(searchTerm.toLowerCase()) ||
        tool.tagKeys.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))
      )
    }

    // Filter by category
    if (selectedCategory !== "All") {
      filteredTools = filteredTools.filter(tool => tool.categoryKey === selectedCategory)
    }

    // Sort tools
    switch (sortBy) {
      case "featured":
        filteredTools = filteredTools.sort((a, b) => (b.featured ? 1 : 0) - (a.featured ? 1 : 0))
        break
      case "rating":
        filteredTools = filteredTools.sort((a, b) => b.rating - a.rating)
        break
      case "price-low":
        filteredTools = filteredTools.sort((a, b) => a.price - b.price)
        break
      case "price-high":
        filteredTools = filteredTools.sort((a, b) => b.price - a.price)
        break
      case "reviews":
        filteredTools = filteredTools.sort((a, b) => b.reviews - a.reviews)
        break
    }

    setTools(filteredTools)
  }, [searchTerm, selectedCategory, sortBy])

  const handleToolClick = (toolId: number) => {
    navigate(`/marketplace/${toolId}`)
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      {/* Hero Section */}
      <section className="pt-20 pb-12 px-4 bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="max-w-6xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            {t('marketplace.title')}
          </h1>
          <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
            {t('marketplace.subtitle')}
          </p>
        </div>
      </section>

      {/* Filters and Search */}
      <section className="py-8 px-4 bg-white border-b">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            {/* Search */}
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                placeholder={t('marketplace.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>

            {/* Category Filter */}
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger className="w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {categories.map(category => (
                  <SelectItem key={category} value={category}>
                    {category}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Sort */}
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="featured">{t('marketplace.sort.featured')}</SelectItem>
                <SelectItem value="rating">{t('marketplace.sort.rating')}</SelectItem>
                <SelectItem value="price-low">{t('marketplace.sort.priceLow')}</SelectItem>
                <SelectItem value="price-high">{t('marketplace.sort.priceHigh')}</SelectItem>
                <SelectItem value="reviews">{t('marketplace.sort.reviews')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </section>

      {/* Tools Grid */}
      <section className="py-12 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {tools.map((tool) => (
              <Card 
                key={tool.id} 
                className="cursor-pointer hover:shadow-lg transition-shadow duration-200"
                onClick={() => handleToolClick(tool.id)}
              >
                <div className="relative">
                  <img
                    src={tool.image}
                    alt={t(tool.nameKey)}
                    className="w-full h-48 object-cover rounded-t-lg"
                  />
                  {tool.featured && (
                    <Badge className="absolute top-2 left-2 bg-yellow-500">
                      {t('marketplace.featured')}
                    </Badge>
                  )}
                  <Badge className="absolute top-2 right-2 bg-blue-500">
                    {t(tool.categoryKey)}
                  </Badge>
                </div>
                <CardHeader>
                  <CardTitle className="text-lg">{t(tool.nameKey)}</CardTitle>
                  <CardDescription className="line-clamp-2">
                    {t(tool.descriptionKey)}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-1">
                      <Star className="h-4 w-4 text-yellow-500 fill-current" />
                      <span className="text-sm font-medium">{tool.rating}</span>
                      <span className="text-sm text-gray-500">({tool.reviews})</span>
                    </div>
                    <div className="text-right">
                      <div className="text-lg font-bold text-blue-600">
                        ${tool.price}
                      </div>
                      <div className="text-sm text-gray-500">
                        /{tool.priceType}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-gray-600">
                      {t('marketplace.by')} {tool.developer}
                    </div>
                    <Button size="sm" variant="outline">
                      {t('marketplace.viewDetails')}
                      <ArrowRight className="ml-1 h-3 w-3" />
                    </Button>
                  </div>

                  <div className="flex flex-wrap gap-1 mt-3">
                    {tool.tagKeys.slice(0, 3).map((tag, index) => (
                      <Badge key={index} variant="secondary" className="text-xs">
                        {t(tag)}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {tools.length === 0 && (
            <div className="text-center py-12">
              <Package className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {t('marketplace.noResults')}
              </h3>
              <p className="text-gray-600">
                {t('marketplace.noResultsDesc')}
              </p>
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  )
} 